var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { parseComponentId, toBool } from "@utils";
import { UXPContext } from "@core/context/Context";
import ComponentRenderer from "@helper/ComponentRenderer/ComponentRenderer";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import PageEditor from "@core/pages/page-editor/PageEditor";
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
import { getAllPluggableViews, onRegistryChange } from "@core/pages/pluggable-view/pluggableViewRegistry";
import { getMountedObjectTypes, onMountedTabsChange } from "@core/pages/injected-tabs/mountedInjectedTabsRegistry";
import { registerFloatingActionSection, unregisterFloatingActionSection, FloatingActionSectionType } from "@core/floating-actions/floatingActionsRegistry";
const ConfiguredPageBase = ({ pageId, additionalProps, configuredProps, notFoundMessage }) => {
    const context = useContext(UXPContext);
    const { navigate, generateURL, location } = useRouterContext();
    const { configurepage } = useSearchParams();
    const componentInfo = useMemo(() => parseComponentId(pageId), [pageId]);
    const [isEditing, setIsEditing] = useState(false);
    const [hasPluggableViews, setHasPluggableViews] = useState(false);
    const [mountedObjectTypes, setMountedObjectTypes] = useState([]);
    const [isMainPageOverridden, setIsMainPageOverridden] = useState(false);
    const canCustomize = !context.isPublicSession;
    const hasEditableContent = !!pageId;
    useEffect(() => {
        setIsEditing(hasEditableContent && configurepage == '1');
    }, [configurepage, hasEditableContent]);
    const resolveCurrentPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const fullPath = (_a = location === null || location === void 0 ? void 0 : location.pathname) !== null && _a !== void 0 ? _a : '';
        const partialPath = fullPath.startsWith('/view') ? fullPath.slice('/view'.length) : fullPath;
        const { data: routesData } = yield context.executeServiceSafe('System', 'AccountRoute:All', {}, { json: true });
        const routes = Array.isArray(routesData) ? routesData : [];
        const customRoute = routes.find(r => r.Route === partialPath);
        if (customRoute === null || customRoute === void 0 ? void 0 : customRoute.Key) {
            const { data: ymlRoutesData } = yield context.executeServiceSafe('System', 'GetRoutesFromConfigurations', { ExcludeOverridden: '0' }, { json: true });
            const ymlRoutes = Array.isArray(ymlRoutesData) ? ymlRoutesData : [];
            const ymlRoute = ymlRoutes.find(r => r.Route === fullPath);
            const isModified = !!(ymlRoute === null || ymlRoute === void 0 ? void 0 : ymlRoute.PageID) && customRoute.PageID !== ymlRoute.PageID;
            return { type: 'route', key: customRoute.Key, isModified };
        }
        const { data: navData } = yield context.executeServiceSafe('System', 'AccountNavigation:GetAll', {}, { json: true });
        const navLinks = Array.isArray(navData) ? navData : [];
        const navLink = navLinks.find(nl => nl.Link === fullPath);
        if (navLink === null || navLink === void 0 ? void 0 : navLink.Key) {
            const isModified = toBool(navLink.IsModified) && navLink.Source === 'yml';
            return { type: 'navlink', key: navLink.Key, id: navLink.ID, isModified };
        }
        return null;
    }), [context, location === null || location === void 0 ? void 0 : location.pathname]);
    useEffect(() => {
        if (!isEditing) {
            setIsMainPageOverridden(false);
            return;
        }
        resolveCurrentPage().then(rec => {
            var _a;
            setIsMainPageOverridden((_a = rec === null || rec === void 0 ? void 0 : rec.isModified) !== null && _a !== void 0 ? _a : false);
        });
    }, [isEditing, resolveCurrentPage]);
    const handleSaveMainPage = useCallback((newPageId, newConfiguredProps, extraArgs) => __awaiter(void 0, void 0, void 0, function* () {
        const rec = yield resolveCurrentPage();
        if (!rec)
            return { error: "This page's configuration is read-only (defined in YML)." };
        if (rec.type === 'route') {
            return context.executeServiceSafe('System', 'AccountRoute:UpdateRoute', Object.assign({ Key: rec.key, PageID: newPageId, ConfiguredProps: JSON.stringify(newConfiguredProps !== null && newConfiguredProps !== void 0 ? newConfiguredProps : {}) }, extraArgs));
        }
        return context.executeServiceSafe('System', 'AccountNavigation:UpdateLink', Object.assign({ Key: rec.key, PageID: newPageId, ConfiguredProps: JSON.stringify(newConfiguredProps !== null && newConfiguredProps !== void 0 ? newConfiguredProps : {}) }, extraArgs));
    }), [context, resolveCurrentPage]);
    const handleResetMainPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const fullPath = (_a = location === null || location === void 0 ? void 0 : location.pathname) !== null && _a !== void 0 ? _a : '';
        const rec = yield resolveCurrentPage();
        if (!rec)
            return { error: "This page's configuration is read-only (defined in YML)." };
        if (rec.type === 'route') {
            const { data: ymlRoutesData } = yield context.executeServiceSafe('System', 'GetRoutesFromConfigurations', { ExcludeOverridden: '0' }, { json: true });
            const ymlRoutes = Array.isArray(ymlRoutesData) ? ymlRoutesData : [];
            const ymlRoute = ymlRoutes.find(r => r.Route === fullPath);
            if (!(ymlRoute === null || ymlRoute === void 0 ? void 0 : ymlRoute.PageID))
                return { error: 'No original view found in YML for this route.' };
            const result = yield handleSaveMainPage(ymlRoute.PageID, {});
            if (result.error)
                return result;
            return { pageId: ymlRoute.PageID, configuredProps: {} };
        }
        const { data: ymlNavData } = yield context.executeServiceSafe('System', 'GetNavigationFromAppConfigurations', {}, { json: true });
        const wrapper = Array.isArray(ymlNavData) ? ymlNavData[0] : ymlNavData;
        const rawNavLinks = wrapper === null || wrapper === void 0 ? void 0 : wrapper.navigationLinks;
        const rawLinks = Array.isArray(rawNavLinks)
            ? rawNavLinks
            : (typeof rawNavLinks === 'string' ? JSON.parse(rawNavLinks) : []);
        const ymlLink = rawLinks.find(l => l.ID === rec.id);
        const originalPageId = (_b = ymlLink === null || ymlLink === void 0 ? void 0 : ymlLink.PageID) !== null && _b !== void 0 ? _b : null;
        if (!originalPageId)
            return { error: 'No original view found in YML for this page.' };
        // IsModified=0 prevents UpdateLink from auto-setting it back to 1
        const result = yield handleSaveMainPage(originalPageId, {}, { IsModified: '0' });
        if (result.error)
            return result;
        setIsMainPageOverridden(false);
        return { pageId: originalPageId, configuredProps: {} };
    }), [context, location === null || location === void 0 ? void 0 : location.pathname, resolveCurrentPage, handleSaveMainPage]);
    useEffect(() => {
        const updatePluggableViews = () => {
            setHasPluggableViews(getAllPluggableViews().length > 0);
        };
        updatePluggableViews();
        const unsubscribe = onRegistryChange(updatePluggableViews);
        return unsubscribe;
    }, []);
    useEffect(() => {
        const update = () => setMountedObjectTypes(getMountedObjectTypes());
        update();
        return onMountedTabsChange(update);
    }, []);
    useEffect(() => {
        if (canCustomize && hasEditableContent) {
            registerFloatingActionSection({
                id: 'page',
                type: FloatingActionSectionType.Page,
                isEditing: isEditing,
                onEnterEditMode: () => navigate(generateURL({ searchParams: { configurepage: '1' } })),
                onExitEditMode: () => navigate(generateURL({ removeParams: ['configurepage', 'pe_node', 'pe_mode', 'pe_tab'] })),
                customActions: []
            });
        }
        return () => {
            if (canCustomize && hasEditableContent) {
                unregisterFloatingActionSection('page');
            }
        };
    }, [canCustomize, isEditing, hasEditableContent, navigate, generateURL]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_configuredview__container", children: componentInfo ? (_jsx(ComponentRenderer, { moduleId: componentInfo.moduleId, componentId: componentInfo.componentId, type: componentInfo.type, configuredProps: configuredProps, additionalProps: additionalProps, notFoundMessage: notFoundMessage })) : (_jsx("div", { className: "uxpcore_configuredview__not_found", children: notFoundMessage || context.$L('uxp-core.text.unable-to-find-the-view') })) }), canCustomize && isEditing && (_jsx(PageEditor, { mountedObjectTypes: mountedObjectTypes, mainPage: pageId ? { pageId, configuredProps, isOverridden: isMainPageOverridden } : undefined, onSaveMainPage: handleSaveMainPage, onResetMainPage: handleResetMainPage }))] }));
};
/**
 * @export
 * A component to render widgets or UI components by parsing the pageId
 *
 * @example
 * <ConfiguredPage
 *   pageId="com.example.module/widget/LocationDetails"
 *   additionalProps={{ LocationKey: 100 }}
 * />
 */
const ConfiguredPage = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(ConfiguredPageBase, Object.assign({}, props)) })));
export default ConfiguredPage;
